import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/bkfmz2dq_ChatGPT%20Image%20Feb%208%2C%202026%2C%2005_38_07%20PM.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
];

const socialLinks = [
  { icon: 'fa-facebook-f', url: 'https://facebook.com', label: 'Facebook' },
  { icon: 'fa-instagram', url: 'https://instagram.com', label: 'Instagram' },
  { icon: 'fa-twitter', url: 'https://twitter.com', label: 'Twitter' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-2' 
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex items-center">
            <img 
              src={LOGO_URL} 
              alt="Last Upholstery" 
              className="h-12 md:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`nav-link font-body text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-gold'
                    : isScrolled ? 'text-leather hover:text-gold' : 'text-white hover:text-gold'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Social Icons - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.icon}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    isScrolled 
                      ? 'bg-leather/10 text-leather hover:bg-gold hover:text-white' 
                      : 'bg-white/20 text-white hover:bg-gold'
                  }`}
                >
                  <i className={`fab ${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>

            {/* Get Quote Button */}
            <Link to="/quote">
              <Button 
                data-testid="header-get-quote-btn"
                className="btn-gold rounded-full px-6 py-2 text-sm font-semibold"
              >
                Get Quote
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isScrolled ? 'text-leather' : 'text-white'
              }`}
            >
              <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t shadow-lg"
          >
            <nav className="container-custom py-6 flex flex-col gap-4">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    className={`block py-2 font-body font-medium text-lg ${
                      location.pathname === link.path ? 'text-gold' : 'text-leather'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <div className="flex gap-4 pt-4 border-t">
                {socialLinks.map((social) => (
                  <a
                    key={social.icon}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-leather/10 flex items-center justify-center text-leather hover:bg-gold hover:text-white transition-all"
                  >
                    <i className={`fab ${social.icon}`}></i>
                  </a>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
